import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Shell from "@/components/app/Shell";
import api from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import { Compass, ArrowRight } from "lucide-react";

const RESULTS = {
  habit: { title: "Sounds like a habit issue.", body: "Your child can do it when asked, but forgets on their own. Focus on passive exercises and daily reminders — mirror checks, palate cues, and steady repetition.", cta: "Review passive exercises" },
  strength: { title: "Sounds like a strength issue.", body: "Muscles are tiring before the task holds. Focus on active exercises with progressive holds and rest days.", cta: "Review active exercises" },
  structural: { title: "This may be a structural roadblock.", body: "If they cannot do it at all, no matter what, a structural factor (tongue tie, tonsils, palate) may be limiting progress. Flag it in the Roadblock Tracker and reach out for a 1:1 evaluation with an experienced provider.", cta: "Open Roadblock Tracker", link: "/roadblocks" },
};

export default function Triage() {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [issue, setIssue] = useState(null);
  const [decision, setDecision] = useState(null);

  useEffect(() => { api.get(`/children/${childId}`).then((r) => setChild(r.data)); }, [childId]);

  const tribe = child ? (TRIBES[child.tribe] || TRIBES.neutral) : TRIBES.neutral;
  const result = decision && RESULTS[decision];

  return (
    <Shell>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest font-semibold flex items-center gap-2" style={{ color: tribe.accent }}><Compass size={12} /> Session 11-12 Tool</div>
          <h1 className="font-display text-3xl font-bold text-navy mt-1">Triage: what's still holding on?</h1>
          <p className="text-ink-mid mt-2">Three questions to point you at the right next step.</p>
        </div>

        {!issue && (
          <div className="bg-warm-white border rounded-3xl p-8" style={{ borderTop: `3px solid ${tribe.accent}`, borderColor: `${tribe.accent}25` }}>
            <div className="font-display text-lg font-bold text-navy">What's still hard?</div>
            <div className="mt-4 grid gap-3">
              <button data-testid="triage-issue-mouth" onClick={() => setIssue("mouth_breathing")} className="text-left p-4 rounded-2xl border transition-colors hover:border-current" style={{ borderColor: `${tribe.accent}40`, color: tribe.accent }}><span className="text-ink-dark">Mouth breathing / tongue positioning</span></button>
              <button data-testid="triage-issue-swallow" onClick={() => setIssue("swallowing")} className="text-left p-4 rounded-2xl border transition-colors hover:border-current" style={{ borderColor: `${tribe.accent}40`, color: tribe.accent }}><span className="text-ink-dark">Swallowing / tongue thrust</span></button>
            </div>
          </div>
        )}

        {issue && !decision && (
          <div className="bg-warm-white border rounded-3xl p-8" style={{ borderTop: `3px solid ${tribe.accent}`, borderColor: `${tribe.accent}25` }}>
            <div className="font-display text-lg font-bold text-navy">Which describes it best?</div>
            <div className="mt-4 grid gap-3">
              <button data-testid="triage-habit" onClick={() => setDecision("habit")} className="text-left p-4 rounded-2xl border transition-colors hover:border-current" style={{ borderColor: `${tribe.accent}40`, color: tribe.accent }}><span className="text-ink-dark">Can do it when asked, but doesn't happen on its own</span></button>
              <button data-testid="triage-strength" onClick={() => setDecision("strength")} className="text-left p-4 rounded-2xl border transition-colors hover:border-current" style={{ borderColor: `${tribe.accent}40`, color: tribe.accent }}><span className="text-ink-dark">Feels hard to sustain / muscles get tired</span></button>
              <button data-testid="triage-structural" onClick={() => setDecision("structural")} className="text-left p-4 rounded-2xl border transition-colors hover:border-current" style={{ borderColor: `${tribe.accent}40`, color: tribe.accent }}><span className="text-ink-dark">Cannot do it at all, no matter what</span></button>
            </div>
            <button className="mt-6 text-sm text-ink-mid underline" onClick={() => setIssue(null)}>Back</button>
          </div>
        )}

        {result && (
          <div className="bg-warm-white rounded-3xl p-8 border-2" style={{ borderColor: tribe.accent }} data-testid="triage-result">
            <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tribe.accent }}>Result</div>
            <h2 className="font-display text-2xl font-bold text-navy mt-1">{result.title}</h2>
            <p className="text-ink-mid mt-3 leading-relaxed">{result.body}</p>
            {result.link ? (
              <Link to={result.link} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm" style={{ background: tribe.accent, color: tribe.panel_ink }}>
                {result.cta} <ArrowRight size={14} />
              </Link>
            ) : (
              <Link to={`/child/${childId}`} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm" style={{ background: tribe.accent, color: tribe.panel_ink }}>
                {result.cta} <ArrowRight size={14} />
              </Link>
            )}
            <button className="ml-3 mt-6 text-sm text-ink-mid underline" onClick={() => { setDecision(null); setIssue(null); }}>Start over</button>
          </div>
        )}
      </div>
    </Shell>
  );
}
